import { useCallback, useEffect, useState } from "react";

const STICKY_APP_KEY = "oneads.app_settings.app_id";
// Same-tab broadcast so sibling instances mounted at the same time (e.g. the
// merged Coefficients & Goals screen renders two app-scoped sections) stay in
// sync live — a localStorage write alone doesn't notify other React states in
// the same document.
const STICKY_EVENT = "oneads:app-settings-app-changed";

function readStored(): number | null {
  try {
    const raw = localStorage.getItem(STICKY_APP_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * Shared app selection across ALL App Settings sub-tabs. The picked app is
 * persisted in localStorage, so choosing an app in one tab pre-selects it in
 * the others. Reactive across instances: a change made in one mounted section
 * propagates live to every other section (same tab via a CustomEvent, other
 * tabs via the storage event). When `apps` is provided it also auto-picks the
 * first app if nothing is stored, and falls back to the first app if the stored
 * one isn't in the current org's list (e.g. after an org switch).
 */
export function useStickyAppId(
  apps: { id: number }[] | undefined,
): [number | null, (id: number | null) => void] {
  const [appId, setAppIdState] = useState<number | null>(readStored);

  const setAppId = useCallback((id: number | null) => {
    setAppIdState(id);
    try {
      if (id == null) localStorage.removeItem(STICKY_APP_KEY);
      else localStorage.setItem(STICKY_APP_KEY, String(id));
    } catch {
      /* ignore storage errors */
    }
    try {
      window.dispatchEvent(new CustomEvent(STICKY_EVENT, { detail: id }));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const onCustom = (e: Event) => {
      const id = (e as CustomEvent<number | null>).detail;
      setAppIdState(id ?? null);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === STICKY_APP_KEY) setAppIdState(readStored());
    };
    window.addEventListener(STICKY_EVENT, onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(STICKY_EVENT, onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    if (!apps || apps.length === 0) return;
    if (appId == null || !apps.some((a) => a.id === appId)) {
      setAppId(apps[0].id);
    }
  }, [apps, appId, setAppId]);

  return [appId, setAppId];
}
